import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import SupportWidget from "@/components/SupportWidget";
import { LayoutDashboard, KanbanSquare, Settings, LogOut, Shield, ExternalLink, Sparkles, X, Store, TrendingUp } from "lucide-react";

export default function AppShell({ children, title, subtitle, actions, company }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [subStatus, setSubStatus] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem("upgrade_banner_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!user || user.role !== "company_owner") return;
    api.get("/billing/me").then(({ data }) => {
      setSubStatus(data?.subscription?.status || "inactive");
    }).catch(() => {});
  }, [user]);

  const dismissBanner = () => {
    setBannerDismissed(true);
    try { sessionStorage.setItem("upgrade_banner_dismissed", "1"); } catch {}
  };

  const showUpgradeBanner = subStatus === "free_claimed" && !bannerDismissed;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200">
        <div className="px-6 py-6 border-b border-slate-100">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#0F172A] flex items-center justify-center text-white">
              <span className="font-display font-bold text-xs">WSQ</span>
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 leading-tight text-sm">WindowScreenQuote</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Pro Dashboard</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
          <NavItem to="/leads" icon={KanbanSquare} label="Quotes" testid="nav-leads" />
          <NavItem to="/sources" icon={TrendingUp} label="Sources" testid="nav-sources" />
          <NavItem to="/settings?tab=marketplace" icon={Store} label="Marketplace" testid="nav-marketplace" badge="NEW" matchPath="/settings" matchSearch="tab=marketplace" />
          <NavItem to="/settings" icon={Settings} label="Settings" testid="nav-settings" excludeSearch="tab=marketplace" />
          {user?.role === "platform_admin" && (
            <NavItem to="/admin" icon={Shield} label="Super Admin" testid="nav-admin" />
          )}
        </nav>

        {company?.slug && (
          <div className="px-4 py-3 mx-3 mb-3 rounded-xl bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Your quote page</div>
            <a
              href={`/${company.slug}/quote`}
              target="_blank"
              rel="noreferrer"
              data-testid="funnel-link"
              className="text-xs font-medium text-[#C75D33] flex items-center gap-1 hover:underline break-all"
            >
              /{company.slug}/quote <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
        )}

        <div className="px-3 py-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-lg bg-slate-200 text-slate-700 flex items-center justify-center font-semibold text-sm">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center text-white font-display font-bold text-xs">WSQ</div>
          <span className="font-display font-bold text-slate-900 text-sm">WindowScreenQuote</span>
        </Link>
        <Button data-testid="mobile-logout-btn" variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }}>
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 grid grid-cols-4">
        <MobileNavItem to="/dashboard" icon={LayoutDashboard} label="Home" testid="mnav-dashboard" />
        <MobileNavItem to="/leads" icon={KanbanSquare} label="Quotes" testid="mnav-leads" />
        <MobileNavItem to="/sources" icon={TrendingUp} label="Sources" testid="mnav-sources" />
        <MobileNavItem to="/settings?tab=marketplace" icon={Store} label="Marketplace" testid="mnav-marketplace" badge="NEW" matchSearch="tab=marketplace" />
        <MobileNavItem to="/settings" icon={Settings} label="Settings" testid="mnav-settings" excludeSearch="tab=marketplace" />
      </nav>

      <main className="flex-1 min-w-0 pt-16 lg:pt-0 pb-20 lg:pb-0">
        {showUpgradeBanner && (
          <div className="bg-gradient-to-r from-[#C75D33] to-[#A14828] text-white" data-testid="free-claim-banner">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
              <Sparkles className="w-4 h-4 shrink-0" />
              <div className="flex-1 text-sm font-medium leading-snug">
                You're on the free <span className="font-bold">Claim Plan</span>.
                <span className="hidden sm:inline"> Start your $1 trial to unlock marketplace leads + your public quote page →</span>
              </div>
              <Link
                to="/billing"
                className="bg-white text-[#C75D33] hover:bg-orange-50 transition px-3 py-1 rounded-lg text-xs font-bold shrink-0"
                data-testid="free-claim-upgrade-btn"
              >
                Start $1 Trial
              </Link>
              <button
                onClick={dismissBanner}
                className="hover:bg-white/10 rounded p-1 shrink-0"
                aria-label="Dismiss"
                data-testid="free-claim-banner-dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          {(title || actions) && (
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
              <div>
                {title && <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="page-title">{title}</h1>}
                {subtitle && <p className="text-slate-500 mt-1.5">{subtitle}</p>}
              </div>
              {actions && <div className="flex items-center gap-3">{actions}</div>}
            </header>
          )}
          {children}
        </div>
      </main>
      {/* Global support widget — only renders for company_owner users */}
      <SupportWidget user={user} />
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testid, badge, matchPath, matchSearch, excludeSearch }) {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const basePath = matchPath || to.split("?")[0];
  let active = pathname.startsWith(basePath);
  if (active && matchSearch && !search.includes(matchSearch)) active = false;
  if (active && excludeSearch && search.includes(excludeSearch)) active = false;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {badge && (
        <span
          data-testid={`${testid}-badge`}
          className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${
            active ? "bg-white/20 text-white" : "bg-[#C75D33] text-white"
          }`}
        >
          {badge}
        </span>
      )}
    </Link>
  );
}

function MobileNavItem({ to, icon: Icon, label, testid, badge, matchSearch, excludeSearch }) {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const basePath = to.split("?")[0];
  let active = pathname.startsWith(basePath);
  if (active && matchSearch && !search.includes(matchSearch)) active = false;
  if (active && excludeSearch && search.includes(excludeSearch)) active = false;
  return (
    <Link to={to} data-testid={testid} className={`relative flex flex-col items-center justify-center py-2.5 ${active ? "text-[#C75D33]" : "text-slate-500"}`}>
      <Icon className="w-5 h-5" />
      <span className="text-[10px] mt-1 font-medium">{label}</span>
      {badge && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute top-1 right-3 text-[8px] font-extrabold uppercase tracking-widest bg-[#C75D33] text-white px-1 py-0.5 rounded leading-none"
        >
          {badge}
        </span>
      )}
    </Link>
  );
}
